// In-memory search connector for react-search-ui (D1 / README §3.4). Answers
// queries synchronously from the shipped payload — the injected data-chart-for
// rows already read by the Table/grouped dropdown/axis switcher — instead of hitting
// OpenSearch, so filter/facet interactions run with no network round-trip. It's
// a drop-in replacement for ElasticsearchAPIConnector on searchConfig.apiConnector.
//
// Faceting mirrors the live behaviour: facets are disjunctive (a facet's own
// selection doesn't collapse its options), base filters on facet fields narrow
// both hits and facet buckets, and base filters on non-facet fields narrow only
// hits.

function matchFilter(row, filter) {
  const wanted = filter.values || [];
  if (wanted.length === 0) return true;
  const value = row?.[filter.field];
  const has = wanted.some(w => String(w) === String(value));
  return filter.type === 'none' ? !has : has;
}

function applyRowFilters(rows, filters) {
  if (!filters || filters.length === 0) return rows;
  return rows.filter(row => filters.every(filter => matchFilter(row, filter)));
}

function distinctValues(rows, field) {
  const seen = new Set();
  rows.forEach(row => {
    const value = row?.[field];
    if (value != null && value !== '') seen.add(String(value));
  });
  return Array.from(seen);
}

// Synchronous, in-memory equivalent of useOpenSearchBootstrap: computes the
// default facet selections (cascading dropdown/radio -> first / default value,
// checkboxes -> valid defaults) and the full value set per facet, from the
// payload rows. No network. `filters` are the facet filter configs; EVERY
// `baseFilter` scopes the value sets, including ones on fields with no facet of
// their own — they decide which rows the chart can plot, so a default chosen
// outside them would open on an empty chart.
export function inMemoryBootstrap(rows, filters, baseFilters = []) {
  const filterList = filters || [];
  const baseScoped = applyRowFilters(rows, baseFilters);

  const allValues = {};
  filterList.forEach(filter => { allValues[filter.attribute] = distinctValues(baseScoped, filter.attribute); });

  const initialFilters = [];
  filterList.forEach(filter => {
    const available = distinctValues(applyRowFilters(baseScoped, initialFilters), filter.attribute);
    if (available.length === 0) return;

    let selectedValues = null;
    if (filter.kind === 'radio' || filter.kind === 'dropdown') {
      if (filter.defaultValues?.length > 0) {
        selectedValues = [filter.defaultValues.find(v => available.includes(v))].filter(Boolean);
      }
      if (!selectedValues?.length) {
        selectedValues = [[...available].sort((a, b) => a.localeCompare(b))[0]];
      }
    } else if (filter.kind === 'checkboxes' && filter.defaultValues?.length > 0) {
      const validDefaults = filter.defaultValues.filter(v => available.includes(v));
      if (validDefaults.length > 0) selectedValues = validDefaults;
    }

    if (selectedValues?.length > 0) {
      initialFilters.push({ field: filter.attribute, values: selectedValues, type: 'any' });
    }
  });

  return { initialFilters, allValues };
}

function toResult(row) {
  const result = {};
  Object.entries(row).forEach(([key, value]) => { result[key] = { raw: value }; });
  return result;
}

export function createInMemoryConnector(rows, { baseFilters = [], facetFields = new Set() } = {}) {
  return {
    onResultClick() {},
    onAutocompleteResultClick() {},
    async onAutocomplete() {
      return { autocompletedResults: [] };
    },
    async onSearch(state, queryConfig) {
      const uiFilters = state.filters || [];

      // Hits: every base scope + every UI filter.
      const hitRows = applyRowFilters(rows, [...baseFilters, ...uiFilters]);

      // Facet buckets: disjunctive — each facet ignores its own UI selection but
      // honours the other filters and EVERY base scope, including scopes on
      // fields with no facet of their own. Counting without those advertises
      // values the chart cannot draw: with a spec_decoding scope of off/mtp, a
      // hardware whose rows are all `eagle` still showed a count, and selecting
      // it emptied the chart. Iterate the
      // connector's own facetFields, NOT queryConfig.facets: react-search-ui strips
      // the disjunctive facets out of the main request's config (it means to fill
      // them via separate per-facet requests), so keying off queryConfig returned
      // no buckets whenever the initial state already carried a filter — e.g. a
      // shared permalink — leaving hits but every facet empty (the sidebar facets
      // then hid themselves via useFacetHasData).
      const facets = {};
      facetFields.forEach(field => {
        const scoped = applyRowFilters(rows, [
          ...baseFilters,
          ...uiFilters.filter(filter => filter.field !== field),
        ]);
        const counts = new Map();
        scoped.forEach(row => {
          const value = row?.[field];
          if (value == null || value === '') return;
          // Key as a string to match distinctValues (initial/allValues) and the
          // live path, whose aggregation keys are always strings. Keying on the
          // raw JSON value would make a numeric field's buckets (8) fail
          // search-ui's strict deep-equal against its filter values ("8") —
          // defaults render unselected and CheckboxFacet emits phantom rows.
          const key = String(value);
          counts.set(key, (counts.get(key) || 0) + 1);
        });
        const data = Array.from(counts, ([value, count]) => ({ value, count }))
          .sort((a, b) => String(a.value).localeCompare(String(b.value)))
          .slice(0, 500);
        facets[field] = [{ field, type: 'value', data }];
      });

      // The chart is not paginated: return every matching row so all points
      // render and axis switcher/grouped dropdown options see the full set. Honouring
      // resultsPerPage (default 250 from providerConfig) would silently truncate
      // a larger collection. totalResults tracks results.length so a consumer
      // never reads a phantom "showing N of M".
      const results = hitRows.map(toResult);

      return {
        results,
        facets,
        totalResults: results.length,
        totalPages: 1,
        pagingStart: results.length ? 1 : 0,
        pagingEnd: results.length,
        requestId: '',
        resultSearchTerm: state.searchTerm || '',
        wasSearched: true,
        rawResponse: {},
      };
    },
  };
}
